import Modal from "components/elements/Modal";
import classes from "./classes.module.scss";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { DocumentArrowUpIcon } from "@heroicons/react/24/outline";
import Button, { EButtonVariant, EButtonColor } from "components/elements/Button";
import Typography from "components/elements/Typography";
import I18n from "components/materials/I18n";
import { ChangeEvent, useCallback, useEffect, useState } from "react";
import Select from "components/materials/Form/SelectElement";
import Input from "components/materials/Form/InputElement";
import DragAndDropFile from "components/materials/Form/DragAndDropFileElement";
import Form, { useForm } from "components/materials/Form";
import MediaRequestResource from "common/resources/Project/Config/Media/MediaRequestResource";
import { I18nTextResource } from "common/resources/I18nText/I18nTextResource";
import { EMediaKind } from "common/enums/Project/Config/Media/EMediaKind";
import { ValidationError } from "common/resources/Resource";
import MediaService from "services/projects/MediaService";
import { container } from "tsyringe";
import MediaResponseResource from "common/resources/Project/Config/Media/MediaResponseResource";
import { translateISO369 } from "utils/I18nText";
import { formatUrl } from "utils/StringUtils";

const mediaService = container.resolve(MediaService);

type IProps = {
	media: MediaResponseResource;
	projectId: string;
	isOpen: boolean;
	onEditedMedia: (updatedMedia: MediaResponseResource) => void;
	onClose: () => void;
};

export default function AddMediaModal(props: IProps) {
	const [customErrors, setCustomErrors] = useState<ValidationError[]>([]);
	const { errors, clearErrors, handleError, formRef } = useForm();
	const [mediaType, setMediaType] = useState<EMediaKind>(props.media.kind);
	const [imageFile, setImageFile] = useState<File | null>(null);

	const onSelectChange = useCallback((event: ChangeEvent<HTMLSelectElement>) => {
		setMediaType(event.target.value as EMediaKind);
	}, []);

	const onSubmit = useCallback(
		(formData: { [key: string]: unknown }) => {
			clearErrors();

			const caption = "caption" in formData ? (formData["caption"] as string) : null;
			if (caption && caption.length > 100) {
				setCustomErrors(getCustomTitleError());
				return;
			}

			const hydratedCaption = caption
				? I18nTextResource.hydrateArray<I18nTextResource>([
						{
							lang: I18n.getLang(),
							text: caption,
						},
					])
				: undefined;

			const hydratedMedia = MediaRequestResource.hydrate<MediaRequestResource>({
				caption: hydratedCaption,
				kind: mediaType,
				previewImageUrl: "previewImageUrl" in formData ? (formData["previewImageUrl"] as string) : undefined,
				url: "url" in formData ? formatUrl(formData["url"] as string) : undefined,
			});

			const file = (formData["file"] as FileList)?.[0];

			mediaService
				.update(props.projectId, props.media.id, hydratedMedia, file)
				.then((updatedMedia) => {
					props.onEditedMedia(updatedMedia);
				})
				.catch(handleError);
		},
		[mediaType, props, clearErrors, handleError],
	);

	useEffect(() => {
		setMediaType(props.media.kind);
	}, [props]);

	useEffect(() => {
		const { file, previewImage } = props.media;
		if (file) {
			mediaService
				.downloadFile(props.projectId, props.media.id)
				.then((blob) => {
					setImageFile(new File([blob], file.name, { type: file.mimeType }));
				})
				.catch(console.error);
		} else if (previewImage) {
			mediaService
				.downloadPreviewImage(props.projectId, props.media.id)
				.then((blob) => {
					setImageFile(new File([blob], previewImage.name, { type: previewImage.mimeType }));
				})
				.catch(console.error);
		}
	}, [props]);

	return (
		<Modal isOpen={props.isOpen} onClose={props.onClose} className={classes["root"]}>
			<div className={classes["header"]}>
				<Typography type="p" size="large" weight="bold">
					{I18n.asset.pages.professional_space.tabs.media.edit_media_modal.title}
				</Typography>
				<XMarkIcon className={classes["icon"]} onClick={props.onClose} />
			</div>

			<Form className={classes["form-container"]} onSubmit={onSubmit} errors={[...customErrors, ...errors]} formRef={formRef}>
				<Select
					name="type"
					onChange={onSelectChange}
					label={I18n.asset.pages.professional_space.tabs.media.edit_media_modal.type}
					defaultValue={mediaType}
					options={getSelectOptions()}
					className={classes["input"]}
				/>

				<Input
					name="caption"
					defaultValue={props.media.caption ? translateISO369(props.media.caption) : null}
					label={I18n.asset.pages.professional_space.tabs.media.edit_media_modal.media_title}
					additionalInfo={I18n.asset.pages.professional_space.tabs.media.edit_media_modal.media_title_max_length}
					className={classes["input"]}
				/>

				{(mediaType === EMediaKind.URL || mediaType === EMediaKind.VIDEO) && (
					<Input name="url" defaultValue={props.media.url} label={I18n.asset.pages.professional_space.tabs.media.edit_media_modal.url} className={classes["input"]} />
				)}

				{mediaType === EMediaKind.IMAGE && (
					<DragAndDropFile
						formRef={formRef}
						name="file"
						defaultFile={imageFile}
						icon={<DocumentArrowUpIcon className={classes["drag-and-drop-icon"]} />}
						accept={["image/png", "image/jpeg", "image/webp"]}
						sub_description={`(max. 5${I18n.asset.unit.megabyte})`}
						maxMbSize={5}
						label={I18n.asset.pages.professional_space.tabs.media.edit_media_modal.drag_and_drop.image}
						description={I18n.asset.pages.professional_space.tabs.media.edit_media_modal.drag_and_drop.description}
						className={classes["drag-and-drop"]}
					/>
				)}

				{(mediaType === EMediaKind.URL || mediaType === EMediaKind.VIDEO) && (
					<DragAndDropFile
						formRef={formRef}
						name="file"
						defaultFile={imageFile}
						icon={<DocumentArrowUpIcon className={classes["drag-and-drop-icon"]} />}
						accept={["image/png", "image/jpeg", "image/webp"]}
						sub_description={`(max. 5${I18n.asset.unit.megabyte})`}
						maxMbSize={5}
						label={I18n.asset.pages.professional_space.tabs.media.edit_media_modal.drag_and_drop.image_link}
						description={I18n.asset.pages.professional_space.tabs.media.edit_media_modal.drag_and_drop.description}
						className={classes["drag-and-drop"]}
					/>
				)}

				<div className={classes["buttons-container"]}>
					<Button variant={EButtonVariant.TEXT} color={EButtonColor.PRIMARY} onClick={props.onClose}>
						{I18n.asset.pages.professional_space.tabs.media.edit_media_modal.cancel}
					</Button>
					<Button variant={EButtonVariant.CONTAINED} color={EButtonColor.PRIMARY} type="submit">
						{I18n.asset.pages.professional_space.tabs.media.edit_media_modal.edit_media}
					</Button>
				</div>
			</Form>
		</Modal>
	);
}

function getSelectOptions() {
	return Object.values(EMediaKind).map((value) => ({ value, label: I18n.asset.pages.professional_space.tabs.media.edit_media_modal[value] }));
}

function getCustomTitleError() {
	return [
		{
			property: "title",
			constraints: {
				maxLength: "The text length must not exceed {{var1}} characters.",
			},
			contexts: {
				maxLength: [100],
			},
		},
	];
}
